import { faqs, stats } from './content'
import type { FaqItem, Stat } from './content'

export const kitHero = {
  subheadline: '100 visuelle Übungsübersichten + 6 Praxispakete für deinen Traineralltag',
  description:
    '100 fertige Übungserklärungen, 24 anpassbare Trainingseinheiten und Korrekturhinweise zu 50 typischen Technikfehlern. Alles an einem Ort, damit du weniger vorbereiten musst und Übungen verständlicher erklären kannst.',
}

export const kitStats: Stat[] = stats

export interface Kit {
  number: string
  title: string
  hook: string
  description: string
}

export const kitsCopy = {
  title: 'Dazu erhältst du 6 Praxispakete für deinen Traineralltag.',
  subtitle:
    'Sechs praktische Hilfen, mit denen du Übungen erklärst, Trainingseinheiten vorbereitest und die Betreuung deiner Kunden organisierst.',
  badge: 'Inklusive',
  closing:
    'Von der Übungserklärung bis zum ersten Training mit einem neuen Kunden: sechs praktische Hilfen für einen besser organisierten Traineralltag.',
  closingNote: 'Alle 6 Praxispakete sind im Komplettpaket enthalten.',
}

export const kits: Kit[] = [
  {
    number: '01',
    title: '100 fertige Übungserklärungen für deine Kunden',
    hook: 'Die passenden Worte für jede Übung.',
    description:
      '100 vorformulierte Erklärungen zu beteiligten Muskeln, Ausführung und praktischen Trainingshinweisen. Wähle die passende Erklärung und passe sie an den Wissensstand deines Kunden an.',
  },
  {
    number: '02',
    title: 'Übungsalternativen auf einen Blick',
    hook: 'Finde eine passende Alternative, ohne lange zu suchen.',
    description:
      'Eine übersichtliche Tabelle mit Übungsvarianten, geordnet nach Bewegungsmuster, Trainingsniveau und verfügbarer Ausrüstung. So findest du leichter eine passende Option für deine Einheit.',
  },
  {
    number: '03',
    title: '24 Trainingseinheiten zum Anpassen',
    hook: 'Deine nächste Einheit beginnt mit einer fertigen Vorlage.',
    description:
      '24 Vorlagen mit Übungen, Sätzen, Wiederholungen und einer festgelegten Reihenfolge. Wähle eine Einheit und passe sie an die Ziele und Voraussetzungen deines Kunden an.',
  },
  {
    number: '04',
    title: '50 typische Technikfehler mit Korrekturhinweisen',
    hook: 'Wissen, worauf du achten und wie du Korrekturen erklären kannst.',
    description:
      '50 häufige Fehler in der Übungsausführung mit praktischen Hinweisen. Nutze sie, um genauer hinzuschauen und deinen Kunden verständliche Rückmeldungen zu geben.',
  },
  {
    number: '05',
    title: 'Vorlagen für Kundendaten und Trainingsprotokolle',
    hook: 'Behalte Training und Fortschritte im Blick.',
    description:
      'Vorlagen zum Dokumentieren von Trainingseinheiten, Gewichten, Wiederholungen und Fortschritten. So hast du wichtige Informationen für die weitere Trainingsplanung griffbereit.',
  },
  {
    number: '06',
    title: 'Leitfaden für die erste Trainingseinheit',
    hook: 'Mit einem klaren Ablauf ins erste Training.',
    description:
      'Checklisten, Vorlagen zur Zielklärung und ein Ablauf für das Erstgespräch und die erste Einheit. Damit du neue Kunden strukturiert betreuen und dich gezielt vorbereiten kannst.',
  },
]

export interface KitOfferRow {
  title: string
  detail: string
}

export const kitOffer = {
  label: 'Das Komplettpaket für Personal Trainer',
  name: 'Visueller Atlas der Trainingsbiomechanik',
  summary: '100 visuelle Übungsübersichten + 6 Praxispakete',
  status: 'Inklusive',
  bundle: '1 vollständiger Atlas + 6 Praxispakete',
  bundleNote: 'Alles übersichtlich gebündelt. Einmal zahlen. Kein Abo.',
  priceLabel: 'Das komplette Paket für nur',
  price: '19',
  currency: '€',
  priceNote: 'Einmalzahlung. Dauerhafter Zugang.',
  cta: 'Ja, ich möchte das Komplettpaket',
  note: 'Sichere Zahlung · Sofortiger Download · 7-Tage-Geld-zurück-Garantie',
  closing:
    '100 analysierte Übungen und 6 praktische Hilfen für deinen Traineralltag. Zusammen in einem digitalen Paket.',
}

export const kitOfferRows: KitOfferRow[] = [
  {
    title: 'Visueller Atlas – 100 Übungsübersichten',
    detail: 'Muskeln, Ausführung, Bewegungsebenen, typische Fehler und praktische Trainingstipps.',
  },
  {
    title: 'Übungserklärungen & Alternativen',
    detail: '100 fertige Erklärungen + eine Übersicht mit Übungsalternativen.',
  },
  {
    title: 'Trainingseinheiten & Technikkorrekturen',
    detail: '24 anpassbare Trainingseinheiten + Hinweise zu 50 typischen Technikfehlern.',
  },
  {
    title: 'Kundenbetreuung & Trainingsstart',
    detail: 'Trainingsprotokolle + Vorlagen für das Erstgespräch und die erste Einheit.',
  },
  {
    title: 'Vollständiger digitaler Zugang',
    detail: 'Druckbare PDFs · Sofortiger Zugang · Dauerhaft nutzbar',
  },
]

export const kitFinalCta = {
  title: '100 analysierte Übungen. 6 Praxispakete. Alles an einem Ort.',
  subtitle:
    'Schlage Übungen nach, bereite deine Trainingseinheiten vor und behalte die Betreuung deiner Kunden im Blick. Mit einem digitalen Paket, das dir wichtige Informationen und praktische Vorlagen griffbereit zur Verfügung stellt.',
  summary: '100 visuelle Übungsübersichten + 6 Praxispakete',
  cta: 'Jetzt Komplettpaket sichern',
}

export const kitFaqs: FaqItem[] = faqs.map((faq) =>
  faq.question === 'Sind fertige Trainingspläne enthalten?'
    ? {
        question: 'Sind bereits vorbereitete Trainingseinheiten enthalten?',
        answer:
          'Ja. Eines der 6 Praxispakete enthält 24 Trainingseinheiten mit Übungen, Sätzen, Wiederholungen und einer festgelegten Reihenfolge. Sie dienen als Vorlagen, die du an die Ziele, den Trainingsstand und die Voraussetzungen deiner Kunden anpasst.',
      }
    : faq,
)
